import {
  aiNoteLanguageOptions,
  type TimesheetAssistant,
  TimesheetAssistantSchema,
} from '@timelyapp/integration-cloud-api-spec';
import { Button } from '@timelyapp/react';
import SimpleSelect from 'components/SimpleSelect';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import {
  getCanManageWorkspaceDefaults,
  getIsUserInExperimentalFeatureGroup,
} from 'redux/selectors/user';
import {
  getHasWorkspaceNoteInstructions,
  getLockedTimesheetAssistantFields,
  getResolvedTimesheetAssistant,
} from '~domains/ai/predictions/redux/predictions.selectors';
import { saveTimesheetAssistantPreferences } from '~domains/ai/preferences/redux/ai.preferences.actions';
import { SummaryIntervalSelect } from '~domains/ai/timesheet_proposals/components/SummaryIntervalSelect';
import { useWithDispatch } from '~hooks/useWithDispatch';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { showToast } from '~redux/modules/toasts.actions';
import styles from './AutoSheetsSettingsRoute.module.scss';
import { ConfirmRow, ENFORCEABLE_SETTINGS_LABEL, FieldRow } from './FieldRow';

const WORKSPACE_AUTOSHEET_SETTINGS_PATH = '/settings/autosheets/workspace';

const effortOptions = [
  { label: 'Low', value: 'none' },
  { label: 'Medium', value: 'low' },
  { label: 'High', value: 'medium' },
];

export const dailyGenerationOptions = [
  { label: 'Every day', value: 'every_day' },
  { label: 'Weekdays only', value: 'weekdays_only' },
  { label: "Don't generate automatically", value: 'off' },
];

const providerOptions = [
  { label: 'Anthropic', value: 'Anthropic' },
  { label: 'OpenAI', value: 'OpenAI' },
];

export const languageOptions = aiNoteLanguageOptions.map(o => ({ label: o.label, value: o.value }));

/**
 * Personal AutoSheet settings: how AutoSheet drafts THIS user's timesheet in
 * this workspace. Every write here is a per-user preference. Settings an
 * admin has enforced render locked for everyone, admins included — an admin
 * changes those on the workspace page, which is the only place that writes
 * the workspace defaults document. Nothing on this page fetches that
 * document: a member learns that note instructions are active from
 * `has_note_instructions` on the resolved preferences, which every member
 * loads anyway.
 */
const AutoSheetsSettingsRoute: React.FC = () => {
  const canManageDefaults = useSelector(getCanManageWorkspaceDefaults);
  const isTimelyEmployee = useSelector(getIsUserInExperimentalFeatureGroup);
  const timelyAccountId = useSelector(getCurrentAccountId);
  const resolved = useSelector(getResolvedTimesheetAssistant);
  const lockedFields = useSelector(getLockedTimesheetAssistantFields);
  const hasWorkspaceNoteInstructions = useSelector(getHasWorkspaceNoteInstructions);

  ticApi.useGetAiWorkspacePreferencesQuery({ timelyAccountId });

  const actions = useWithDispatch({
    showToast,
    saveTimesheetAssistantPreferences,
  });

  const [confirming, setConfirming] = useState<'reset' | 'clear' | null>(null);

  const handleUserPreferenceChange = useCallback(
    async (preferences: Partial<TimesheetAssistant>) => {
      try {
        await actions.saveTimesheetAssistantPreferences({ preferences });
        actions.showToast({ message: 'Setting saved', type: 'success' });
      } catch {
        actions.showToast({ message: 'Failed to save setting', type: 'danger' });
      }
    },
    [actions]
  );

  // Locked is locked for everyone. TIC refuses a per-user write to a locked
  // field, so offering the control to an admin here would only produce an
  // error toast; the workspace page is where those values change.
  const isFieldLocked = useCallback(
    (field: string) => lockedFields.includes(field),
    [lockedFields]
  );
  const hasLockedFields = lockedFields.length > 0;

  const lockedBadge = (field: string) =>
    isFieldLocked(field) ? <span className={styles.lockedBadge}>locked</span> : null;

  const handleResetDefaults = useCallback(() => {
    setConfirming(null);
    const defaults = TimesheetAssistantSchema.parse({});
    // Locked fields are the workspace's, and TIC refuses a per-user write to
    // one; reset only what is this user's to reset.
    const resettable: Partial<TimesheetAssistant> = {
      interval_minutes: defaults.interval_minutes,
      provider: defaults.provider,
      effort: defaults.effort,
      note_language: defaults.note_language,
      daily_generation: defaults.daily_generation,
    };
    for (const field of lockedFields) delete resettable[field as keyof TimesheetAssistant];
    actions.saveTimesheetAssistantPreferences({ preferences: resettable });
    actions.showToast({ message: 'Settings reset to defaults', type: 'success' });
  }, [actions, lockedFields]);

  const handleClearAiMemory = useCallback(() => {
    setConfirming(null);
    actions.saveTimesheetAssistantPreferences({
      preferences: { last_reset_at: new Date().toISOString() },
    });
    actions.showToast({
      message: 'AI memory has been reset, new suggestions will start fresh',
      type: 'success',
    });
  }, [actions]);

  const workspacePageLink = (
    <Link to={WORKSPACE_AUTOSHEET_SETTINGS_PATH}>Workspace › AutoSheet</Link>
  );

  return (
    <>
      <header className={styles.header}>
        <h1>AutoSheet</h1>
        <p>How AutoSheet drafts your timesheet and writes your notes in this workspace.</p>
      </header>
      <div className={styles.container}>
        {hasLockedFields && (
          <div className={styles.enforcedBanner}>
            {canManageDefaults ? (
              <>
                Settings marked locked are set for everyone in this workspace, you included. Change
                them under {workspacePageLink}.
              </>
            ) : (
              'Settings marked locked have been set for the whole workspace by an admin, so you cannot choose your own.'
            )}
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Generation</div>
          <div className={styles.sectionDescription}>
            When AutoSheet drafts your timesheet, and how finely it splits your day into entries.
          </div>

          <FieldRow
            label={
              <>
                Time entry length
                {lockedBadge('interval_minutes')}
              </>
            }
            hint="Tracked activity is grouped into time entries of at least this length."
          >
            <SummaryIntervalSelect
              value={resolved.interval_minutes}
              onChange={v => v !== null && handleUserPreferenceChange({ interval_minutes: v })}
              disabled={isFieldLocked('interval_minutes')}
            />
          </FieldRow>

          <FieldRow
            label={
              <>
                Daily generation
                {lockedBadge('daily_generation')}
              </>
            }
            hint="Which days AutoSheet drafts a timesheet for you. You can always generate one yourself."
          >
            <SimpleSelect
              value={resolved.daily_generation}
              options={dailyGenerationOptions}
              onChange={v =>
                handleUserPreferenceChange({
                  daily_generation: v as TimesheetAssistant['daily_generation'],
                })
              }
              disabled={isFieldLocked('daily_generation')}
              placeholder="Select schedule"
              onCreatePlaceholder=""
            />
          </FieldRow>

          {isTimelyEmployee && (
            <>
              <FieldRow
                label={
                  <>
                    Effort <span className={styles.internalBadge}>internal</span>
                  </>
                }
              >
                <SimpleSelect
                  value={resolved.effort}
                  options={effortOptions}
                  onChange={v =>
                    handleUserPreferenceChange({
                      effort: v as TimesheetAssistant['effort'],
                    })
                  }
                  placeholder="Select effort"
                  onCreatePlaceholder=""
                />
              </FieldRow>

              <FieldRow
                label={
                  <>
                    Provider <span className={styles.internalBadge}>internal</span>
                  </>
                }
              >
                <SimpleSelect
                  value={resolved.provider}
                  options={providerOptions}
                  onChange={v =>
                    handleUserPreferenceChange({
                      provider: v as TimesheetAssistant['provider'],
                    })
                  }
                  placeholder="Select provider"
                  onCreatePlaceholder=""
                />
              </FieldRow>
            </>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Notes</div>
          <div className={styles.sectionDescription}>
            How AutoSheet writes the note on each time entry.
          </div>

          <FieldRow
            label={
              <>
                Note language
                {lockedBadge('note_language')}
              </>
            }
            hint="The language notes are written in."
          >
            <SimpleSelect
              value={resolved.note_language}
              options={languageOptions}
              onChange={v =>
                handleUserPreferenceChange({
                  note_language: v as TimesheetAssistant['note_language'],
                })
              }
              disabled={isFieldLocked('note_language')}
              placeholder="Select language"
              onCreatePlaceholder=""
            />
          </FieldRow>

          {canManageDefaults ? (
            <div className={styles.manageLink}>
              Note instructions for everyone's notes are managed under {workspacePageLink}.
            </div>
          ) : (
            hasWorkspaceNoteInstructions && (
              <div className={styles.sectionNotice}>
                Your workspace admin has set note instructions that shape how AutoSheet writes the
                notes on everyone's time entries in this workspace.
              </div>
            )
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Actions</div>
          <div className={styles.actionRow}>
            <div>
              <div className={styles.actionLabel}>Clear AI memory</div>
              <div className={styles.actionDescription}>
                AutoSheet stops learning from timesheets you accepted before now. New drafts start
                fresh.
              </div>
            </div>
            {confirming !== 'clear' && (
              <Button priority="low" size="small" onClick={() => setConfirming('clear')}>
                Clear memory
              </Button>
            )}
          </div>
          {confirming === 'clear' && (
            <ConfirmRow
              message="Clear AI memory? AutoSheet will stop learning from everything you accepted before now."
              confirmLabel="Clear memory"
              onConfirm={handleClearAiMemory}
              onCancel={() => setConfirming(null)}
            />
          )}
          <div className={styles.actionRow}>
            <div>
              <div className={styles.actionLabel}>Reset to defaults</div>
              <div className={styles.actionDescription}>
                Restore {ENFORCEABLE_SETTINGS_LABEL} to their defaults
                {hasLockedFields && ', except settings locked by your workspace'}.
              </div>
            </div>
            {confirming !== 'reset' && (
              <Button priority="low" size="small" onClick={() => setConfirming('reset')}>
                Reset
              </Button>
            )}
          </div>
          {confirming === 'reset' && (
            <ConfirmRow
              message="Reset your AutoSheet settings to their defaults?"
              confirmLabel="Reset settings"
              onConfirm={handleResetDefaults}
              onCancel={() => setConfirming(null)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AutoSheetsSettingsRoute;
